import dayjs from 'dayjs';
import React, { useCallback, useImperativeHandle, useRef, useState, Ref } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLanguage } from '@travel/i18n';
import RanDataFormInputs from '@travel/traveler-core/components/RanDataFormInputs';
import { useDeviceType, useDialogHandler, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { useDatesChange } from '@travel/traveler-core/hooks/useDatesChange';
import { getRanData } from '@travel/traveler-core/store/ranStore/selectors';
import { FlatButton } from '@travel/ui';
import { cx, parseQueryString, stringify } from '@travel/utils';

import { AmountInputRef } from 'components/AmountInput';
import AmountInputWithRef from 'components/AmountInput/AmountInputWithRef';
import { DateRef } from 'components/DateInput';
import DateInputWithRef from 'components/DateInput/DateInputWithRef';
import Form from 'components/Form';
import useCustomFixedStyling from 'components/Header/useCustomFixedStyling';
import PlaceInput from 'components/PlaceInput';
import SalesPromotionInput from 'components/SalesPromotionInput';
import { EMPTY_ARRAY, EMPTY_FUNCTION } from 'constants/defaultValue';
import usePlaceRedirect from 'hooks/usePlaceRedirect';
import { updateAreaName } from 'store/providerList/actions';
import { getAreaName, getIsFetchingList } from 'store/providerList/selectors';
import { savedSearchStore } from 'utils/savedSearch';
import FilterContainer from '../FilterContainer';
import FilterForm, { FilterFormProps } from '../FilterForm';

import { SortKeysType } from 'constants/provider';
import { Translate } from 'core/translate';
import { SearchFormQuery } from 'ProviderList-Types';
import { SuggestionsItem } from 'Suggestions-Types';
import { getAllowedRedirectSearchQuery } from 'utils/search';

import { getHeightIfSmartBannerPresent } from 'store/banner/selectors';

import styles from './searchForm.module.scss';

export type RoomFormQuery = {
  startDate?: string;
  endDate?: string;
  noOfUnits?: string;
  adults?: string;
  childrenAges?: string;
};

export type SearchFormRef = {
  onSubmit: () => void;
} | null;

export type Props = {
  className?: string;
  /** String of current place id */
  placeId: string;
  /** Object of current query */
  query: SearchFormQuery;
  /** Callback to be called when list style updated */
  onUpdateListView?: () => void;
  /** Callback to be called when list style updated */
  isExpandedMap?: boolean;
  /** Callback to be called when remove button of coupon name is clicked */
  onRemoveCoupon?: () => void;
  /** Filter search form values for PC/TL */
  filterFormProps?: FilterFormProps;
  setSortKey?: React.Dispatch<React.SetStateAction<SortKeysType>>;
  /** inner Ref */
  innerRef?: Ref<SearchFormRef>;
  /** Automatically open the date picker on mount */
  shouldOpenDatePickerOnMount?: boolean;
};

function SearchForm(props: Props) {
  const { shouldOpenDatePickerOnMount } = props;
  const dispatch = useDispatch();
  const areaName = useSelector(getAreaName);
  const isFetching = useSelector(getIsFetchingList);
  const bannerHeight = useSelector(getHeightIfSmartBannerPresent);
  const language = useSelector(getLanguage);
  const ranData = useSelector(getRanData);
  const placeRedirect = usePlaceRedirect();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;

  const [currentPlace, setCurrentPlace] = useState<SuggestionsItem>({
    id: props.placeId,
    pathId: props.placeId,
    type: 'SPOT',
    name: areaName,
    category: 'POINT_OF_INTEREST',
  });

  const { isDatesSet, onDatesSet } = useDatesChange(
    Boolean(props.query?.startDate) && Boolean(props.query?.endDate),
  );
  const [startDate, setStartDate] = useState(props.query.startDate || '');
  const [endDate, setEndDate] = useState(props.query.endDate || '');
  const [isAutoOpenDatePicker, setIsAutoOpenDatePicker] = useState(false);

  const formEl = useRef<HTMLFormElement | null>(null);
  const dateRef = useRef<DateRef>(null);
  const amountInputRef = useRef<AmountInputRef>(null);
  const {
    isOpen: isExpandPanel,
    onToggle: onToggleSearchPanel,
    onClose: onCloseSearchPanel,
  } = useDialogHandler(false);

  const [isError, setIsError] = useState(false);
  const { isCustomFixedBarHidden } = useCustomFixedStyling();

  const getFormQueryString = useCallback(() => {
    if (!formEl.current) {
      return '';
    }

    const formData = new FormData(formEl.current);
    const search = new URLSearchParams(formData as any);
    return search.toString();
  }, []);

  const onSubmit = useCallback(
    (place: SuggestionsItem, shouldResetFilter) => {
      if (!formEl.current) {
        return;
      }
      if (!place.pathId) {
        setIsError(true);
        return;
      }

      let queryString = getFormQueryString();

      if (shouldResetFilter && place.category !== 'PROVIDER') {
        const queryObject = parseQueryString(queryString);
        queryString = stringify(getAllowedRedirectSearchQuery(queryObject));
      }

      // NOTE: Optimistic update area name
      // Because we allow a component to be rendered when the data is not ready
      // The component will be initialized with empty data or prev search data (from store)
      // to keep the latest search text before re-rendering / avoid blinking search input, we have to state search value beforehand
      const formData = new FormData(formEl.current);
      const queryObj = Object.fromEntries(formData.entries());
      if (place.category !== 'PROVIDER') {
        dispatch(updateAreaName(place.name));
        savedSearchStore(language, place, queryObj);
      }
      placeRedirect(place, queryString);
    },
    [dispatch, getFormQueryString, language, placeRedirect],
  );

  useImperativeHandle(props.innerRef, () => ({
    onSubmit: () => {
      onSubmit(currentPlace, false);
    },
  }));

  const onChangePlace = (place: SuggestionsItem) => {
    setCurrentPlace(place);
    if (place.pathId) {
      setIsError(false);

      if (isMobile) {
        if (place.category === 'PROVIDER') {
          placeRedirect(place, getFormQueryString());
        } else {
          // re-fetch the provider list
          onSearchWithNewRoomCondition(place);
        }
      }
    } else {
      if (isMobile && !place.name) {
        setIsError(true);
      }
    }
    if (place.isSavedSearch && place?.query) {
      // setDate
      const startDate = place.query.startDate && place.query.startDate.toString();
      const endDate = place.query.endDate && place.query.endDate.toString();
      // the dates needs to be set for the childrenAges to be set
      let shouldSetAmount = true;
      if (startDate && endDate) {
        if (dayjs(startDate).isSameOrBefore(dayjs().subtract(1, 'day'))) {
          shouldSetAmount = false;
          dateRef.current?.resetDate();
          setStartDate('');
          setEndDate('');
          onDatesSet('', '');
          if (!isMobile) {
            setIsAutoOpenDatePicker(true);
            dateRef.current?.focus();
          }
        } else {
          setStartDate(startDate);
          setEndDate(endDate);
          onDatesSet(startDate, endDate);
        }
      }
      // set rooms/guests/children amount
      if (shouldSetAmount) {
        amountInputRef.current?.setAmount({
          rooms: place.query.noOfUnits ? Number(place.query.noOfUnits) : undefined,
          adults: place.query.adults,
          childrenAges: place.query.childrenAges,
        });
      }
    }
  };

  const onSelectPlace = (isSavedSearch?: boolean, hasDate?: boolean) => {
    if (isSavedSearch && !hasDate && isDatesSet) {
      dateRef.current?.resetDate();
      setStartDate('');
      setEndDate('');
      onDatesSet('', '');
    } else if (!isSavedSearch && !isDatesSet && !isMobile) {
      dateRef.current?.focus();
      setIsAutoOpenDatePicker(true);
    }
  };

  const onSearchWithNewRoomCondition = useCallback(
    (place?: SuggestionsItem) => onSubmit(place || currentPlace, true),
    [currentPlace, onSubmit],
  );
  const onSearchWithFilter = useCallback(() => onSubmit(currentPlace, false), [
    currentPlace,
    onSubmit,
  ]);

  const onPage =
    props.query?.endDate || props.query?.startDate ? 'providerListDated' : 'providerListUndated';

  const placeInput = (
    <div className={cx(styles.placeInput, isMobile && isExpandPanel && styles.active)}>
      <PlaceInput
        onPage={onPage}
        defaultText={areaName || ''}
        onChangePlace={onChangePlace}
        onSelectPlace={onSelectPlace}
        hasError={isError}
        errorMessage={
          <Translate
            id="Traveler_Input_Error.Global_Search.Destination_Search.No_Input"
            className={styles.errorMessage}
          />
        }
        errorMessageClassName={styles.errorMessageWrapper}
      />
    </div>
  );

  const dateInput = (
    <DateInputWithRef
      ref={dateRef}
      className={styles.dateInput}
      onPage={onPage}
      onClickOkButton={onSearchWithNewRoomCondition}
      startDate={startDate}
      endDate={endDate}
      onDateChanged={onDatesSet}
      isAutoOpenDatePicker={isAutoOpenDatePicker}
      setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
      shouldOpenOnMount={!startDate && !endDate && shouldOpenDatePickerOnMount}
    />
  );

  const amountInput = (
    <AmountInputWithRef
      ref={amountInputRef}
      className={styles.amountInput}
      onPage={onPage}
      onClickOkButton={() => onSearchWithNewRoomCondition()}
      defaultAdults={Number(props.query.adults)}
      defaultRooms={Number(props.query.noOfUnits)}
      defaultChildrenAges={
        props.query.childrenAges ? props.query.childrenAges.split(',').map(Number) : EMPTY_ARRAY
      }
      isDatesSet={isDatesSet}
    />
  );

  return (
    <>
      <Form
        ref={formEl}
        className={cx(
          props.className,
          styles.wrapper,
          isCustomFixedBarHidden && styles.fixedBarHidden,
        )}
        data-testid="searchForm-wrapper"
        onSubmit={() => onSearchWithNewRoomCondition()}
        style={isMobile && !isCustomFixedBarHidden ? { marginTop: bannerHeight } : undefined}
      >
        {!isMobile && (
          <>
            <div className={styles.searchFormContainer}>
              <div className={styles.searchForm}>
                {placeInput}
                {dateInput}
                {amountInput}
                <FlatButton
                  className={styles.submitButton}
                  type="submit"
                  isLoading={isFetching}
                  data-testid="providerListPage-searchForm-submitButton"
                >
                  <Translate id="Top.Search_Button" />
                </FlatButton>
              </div>
            </div>
            <FilterForm {...props.filterFormProps} />
          </>
        )}
        {isMobile && (
          <>
            <div className={cx(styles.searchForm, isExpandPanel && styles.active)}>
              <div className={styles.inputsWrapper}>
                {placeInput}
                {dateInput}
                {amountInput}
              </div>
              {!isExpandPanel && (
                <button
                  type="button"
                  className={styles.formActiveButton}
                  onClick={onToggleSearchPanel}
                  aria-label="active search panel"
                  data-testid="searchForm-toggleSearchPanel-button"
                />
              )}
              <FilterContainer
                onPage={onPage}
                className={styles.searchOptionItem}
                pathId={props.placeId}
                query={props.query}
                onSearch={onSearchWithFilter}
                onRemoveCoupon={props.onRemoveCoupon}
                setSortKey={props.setSortKey}
              />
            </div>
          </>
        )}
        <SalesPromotionInput
          salesPromotionId={props.query.salesPromotionId}
          promotionCode={props.query.promotionCode}
          isCouponSpecified={props.query.isCouponSpecified}
        />
        <RanDataFormInputs ranData={ranData} />
      </Form>
      {isExpandPanel && (
        <button
          tabIndex={-1}
          className={styles.overlay}
          onClick={onCloseSearchPanel}
          onKeyDown={EMPTY_FUNCTION}
        />
      )}
    </>
  );
}

export default React.memo(SearchForm);
